import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableSelect Component
 * 
 * A premium, accessible dropdown with real-time search filtering.
 * Uses Radix UI Popover and CMDK for full keyboard support.
 */
const SearchableSelect = React.forwardRef(({
    options = [],
    value,
    onChange,
    placeholder = "Seçiniz...",
    searchPlaceholder = "Ara...",
    emptyMessage = "Sonuç bulunamadı.",
    className,
    disabled = false,
    label
}, ref) => {
    const [open, setOpen] = React.useState(false);

    const selectedOption = React.useMemo(() =>
        options.find((option) => option.value === value),
        [options, value]
    );

    return (
        <div className={cn("grid gap-1.5", className)}>
            {label && (
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider ml-1">
                    {label}
                </label>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            "w-full justify-between bg-[#FAF7F0] border-gray-200 hover:bg-white hover:border-[#D4AF37] text-sm font-normal h-11 rounded-xl transition-all",
                            !value && "text-muted-foreground"
                        )}
                    >
                        {selectedOption ? selectedOption.label : placeholder}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[1100]" align="start">
                    <Command className="bg-white">
                        <CommandInput placeholder={searchPlaceholder} className="h-10" />
                        <CommandList className="max-h-[250px] custom-scrollbar">
                            <CommandEmpty>{emptyMessage}</CommandEmpty>
                            <CommandGroup>
                                {options.map((option) => (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label} // CMDK uses label for internal filtering
                                        onSelect={() => {
                                            onChange(option.value);
                                            setOpen(false);
                                        }}
                                        className="cursor-pointer py-2.5 px-3 aria-selected:bg-[#D4AF37]/10 aria-selected:text-[#D4AF37]"
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4 transition-opacity",
                                                value === option.value ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        {option.label}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
});

SearchableSelect.displayName = "SearchableSelect";

export { SearchableSelect };
